/* eslint-disable no-console */
import React from 'react';

import {
	defaultSorter
} from '../helper/Brood.js';

import {
	shouldSkipSlide,
	isFolderFileActive,
	limitSlidesToSlots
} from '../util/slides.js';

import {
	broncoURLENV,
	condorComponentENV,
	envENV,
} from '../ENV';

import './Pelican.css';

class PelicanProviderComponent extends React.Component {
	// VCL providerName is slide_deck
	static providerName = 'slideshow';

	constructor(props) {
		super(props);

		this.state = {
			slides: [],
			folderLimits: {},
			// Mirror of _folderCursor for devtools / React tracking. Source of
			// truth is the instance shadow.
			folderCursor: {},
		};

		// Cursor-based folder rotation.
		//
		// _folderCursor[folderId] = the 1-based gallery_file_index of the NEXT
		// folder slide to play. Advanced by 1 (with wrap) after every folder
		// slide play OR error. Used by skipper to allow at most one folder
		// slide at a time — the picker iterates and finds the cursor slide.
		//
		// _folderPlaysThisLoop[folderId] = how many folder slides have played
		// successfully in the current VCL loop. Capped at folder_slots: once
		// reached, skipper rejects all folder slides for this folder until the
		// loop resets (a non-folder slide plays).
		//
		// Both must be readable synchronously from skipper — Brood's picker
		// runs synchronously inside slideshow.next() and waits for no React
		// commit. setState mirror exists only for devtools introspection.
		this._folderCursor = {};
		this._folderPlaysThisLoop = {};

		// _pendingFolderSubstitute — folderId currently expecting a substitute
		// folder slide to fill an errored slot. While set, skipper rejects
		// non-folder slides so the picker can't loop-back to 1.png between a
		// folder error and its substitute. Cleared the moment a folder slide
		// from this folder plays successfully.
		this._pendingFolderSubstitute = null;

		this.providerID = `PROVIDER_${props.component_name}`;
	}

	componentDidMount() {
		const {
			updateSlideshowProvider,
			isVisualContentLoop
		} = this.props;

		// don't initialize if we're not in a slideshow
		if (!updateSlideshowProvider) {
			return;
		}

		// NOTE: unlike Nova/Peacock/MockNova/BroadsignFeed we do NOT register
		// an empty provider here. VCL-mode Pelican's fetchSlides is synchronous
		// (reads props.provider.slides — no network) so the failure-to-register
		// case doesn't exist. Adding an empty registration here causes an extra
		// Brood.updateProvider → re-render → componentDidUpdate → initUpdate
		// cycle that resets rotation state mid-loop and corrupts folder rotation.

		this.abortController = new AbortController();
		this.initUpdate();

		if (isVisualContentLoop) {
			return;
		}

		// check if any slide has scheduling changes like
		// time/date/day_of_week and update brood if so
		this.schedulingInterval = setInterval(() => {
			const {slides} = this.state;

			// Update the provider with the new slide states
			const newProvider = this.makeProvider(slides);
			updateSlideshowProvider(newProvider);
			this.setState({slides: slides});
		  }, 60000);
	}

	componentWillUnmount() {
		if (this.abortController) {
			this.abortController.abort();
		}

		// clearInterval(this.updateInterval);
		clearInterval(this.refreshSlidesInterval);
		clearInterval(this.schedulingInterval);
	}

	componentDidUpdate(prevProps) {
		const {
			last_update,
			provider
		} = this.props;

		if (prevProps.last_update !== last_update || prevProps.provider !== provider) {
			this.initUpdate();
		}
	}

	async initUpdate() {
		const {
			updateSlideshowProvider,
		} = this.props;

		// Guard against being called without slideshow context
		if (!updateSlideshowProvider) {
			return;
		}

		try {
			const { slides, folderLimits } = await this.fetchSlides();

			const newProvider = this.makeProvider(slides, folderLimits);
			updateSlideshowProvider(newProvider);
		} catch (err) {
			if (err.name === 'AbortError') {
				return;
			}

			console.error('BROOD::PELICAN::ERROR_initUpdate:', err);
		}
	}

	async fetchSlides() {
		const {
			isVisualContentLoop,
			provider = {}
		} = this.props;

		let folderLimits = {};
		// First-encounter folder ids — used to seed _folderCursor on initial fetch
		// without clobbering an in-flight rotation on a subsequent re-fetch.
		const seenFolderIds = new Set();

		let slides = [];
		if (isVisualContentLoop) {
			slides = provider.slides
				.filter((slide) => {
					return !slide.is_folder_placeholder;
				})
				// flatten folder_slides, filtering per-file schedules
				.flatMap((slide) => {
					if (slide.folder_id) {
						folderLimits[slide.folder_id] = slide.folder_slots;
						seenFolderIds.add(slide.folder_id);

						const activeFiles = slide.folder_slides
							.filter(isFolderFileActive)
							.sort((a, b) => (a.gallery_file_index ?? 0) - (b.gallery_file_index ?? 0));

						const folderLength = activeFiles.length;
						return activeFiles.map((folderSlide, idx) => {
							return {
								...folderSlide,
								folder_id: slide.folder_id,
								folder_length: folderLength,
								gallery_file_index: idx + 1
							};
						});
					}

					return slide;
				})
				.map((slide, i) => {
					slide.slideIndex = i;

					if (provider.category === 'slide_deck' || provider.category === 'published_feed') {
						slide.slideDeckName = provider.name || '';
					}

					return this.makeSlide(slide);
				});

			// Sort by deck Order ASC, tie-break alphabetically by name — mirrors the
			// server-side sort in makeProviderFromSlideDeck and the defaultSorter logic
			// used by non-VCL Pelican. Folder file ordering (within a folder) is driven
			// by gallery_file_index / cursor, not array position, so resorting here is safe.
			slides.sort(defaultSorter);

			// Limit slides to provider.slots, accounting for folders.
			// folderLimits may be adjusted if folders are reduced to fit available slots
			// (e.g. SD slots=2, folder_slots=2, non-folder=1 → folder reduced to 1).
			const limited = limitSlidesToSlots(slides, provider.slots, folderLimits);
			slides = limited.slides;
			folderLimits = limited.folderLimits;

			// Determine which folders actually survived limitSlidesToSlots (folders
			// whose slides got cut by slot exhaustion shouldn't leave cursor/plays
			// state behind — they'd otherwise stall the pure-folder loop-wrap reset,
			// because their playsThisLoop=0 keeps failing the allAtQuota check
			// forever even though their slides aren't in the rotation).
			const survivingFolderIds = new Set();
			for (const s of slides) {
				if (s.folder_id) {
					survivingFolderIds.add(s.folder_id);
				}
			}

			// Purge state for any folder no longer in the rotation — cut by
			// limitSlidesToSlots OR removed from the payload entirely. A stale
			// cursor/plays entry stalls the pure-folder loop-wrap reset (its
			// playsThisLoop never reaches quota), and an orphaned pending
			// substitute makes skipper reject every non-folder slide forever.
			// Object.keys yields strings while folder ids are numbers — compare
			// as strings.
			const survivingFolderKeys = new Set([ ...survivingFolderIds, ].map(String));
			const knownFolderKeys = new Set([
				...Object.keys(this._folderCursor),
				...Object.keys(this._folderPlaysThisLoop),
			]);
			for (const folderKey of knownFolderKeys) {
				if (!survivingFolderKeys.has(folderKey)) {
					delete this._folderCursor[folderKey];
					delete this._folderPlaysThisLoop[folderKey];
				}
			}
			if (this._pendingFolderSubstitute !== null && !survivingFolderKeys.has(String(this._pendingFolderSubstitute))) {
				this._pendingFolderSubstitute = null;
			}

			// Seed cursor/playsThisLoop for any folder we haven't seen before. Mid-loop
			// re-fetches preserve existing values so window state doesn't snap back to
			// the start on every componentDidUpdate.
			for (const folderId of seenFolderIds) {
				if (!survivingFolderIds.has(folderId)) {
					continue;
				}
				if (this._folderCursor[folderId] === undefined) {
					this._folderCursor[folderId] = 1;
				}
				if (this._folderPlaysThisLoop[folderId] === undefined) {
					this._folderPlaysThisLoop[folderId] = 0;
				}
			}

			this.setState({
				slides: slides,
				folderLimits: folderLimits,
				folderCursor: { ...this._folderCursor },
			});

			return { slides, folderLimits };
		}

		const tlcUUID = condorComponentENV;
		const componentUUID = this.props.component_name;

		const body = {
			condor_component_uuid: tlcUUID,
			pelican_uuids:         [componentUUID]
		};

		try {
			const fetchURL = `${broncoURLENV}/pelican`;
			const response = await fetch(fetchURL, {
				body:    JSON.stringify(body),
				headers: {'Content-Type': 'application/json'},
				method:  'POST',
				signal:  this.abortController?.signal
			});

			const jsonData = await response.json();

			for (const slide of Object.values(jsonData)) {
				slides.push(this.makeSlide(slide));
			}
		} catch (err) {
			if (err.name === 'AbortError') {
				throw err; // Re-throw to be handled by initUpdate
			}
			console.error('BROOD::PELICAN::ERROR_fetchSlides:', err);
		}

		slides.sort(defaultSorter);

		slides = slides.map((slide) => {
			return {
				...slide
			};
		});

		this.setState({
			slides: slides
		});

		return { slides, folderLimits: {} };
	}

	makeSlide(slide) {
		const {
			isVisualContentLoop,
			providerID,
		} = this.props;

		const file = slide.file || '';

		let src;
		if (slide.kind === 'html') {
			// An html slide's file is a real URL (also used by the iframe below);
			// keep it verbatim so the asset_url/cache key isn't a bogus S3 path.
			src = file;
		} else if (slide.is_from_library || file.startsWith('uploads/')) {
			// Library and uploads content lives in the ts-library tree.
			src = `https://ts-condor-assets.s3.us-east-1.amazonaws.com/${envENV}/ts-library/${file}`;
		} else {
			// Legacy pelican content: use the stored key as-is. It already carries
			// its own <env>/<uuid>/ prefix, and prepending this kiosk's running env
			// would corrupt any key whose stored env differs from it.
			src = `https://s3.amazonaws.com/ts-pelican/${file}`;
		}

		slide.data = {
			...slide,
			src: src,
			alt: slide.title
		};

		if (slide.kind === 'html') {
			slide.data.html = <iframe
				src={slide.file}
				id={slide.slideID}
				allow='autoplay, geolocation'
				sandbox='allow-scripts allow-same-origin'
				className='pelican-iframe'
				loading='lazy'
			/>;
		}

		slide.allowAudio = !!this.props.allow_audio;

		slide.options = {
			duration: (Number(slide.duration) * 1000)
		};

		slide.type = slide.kind.split('/')[0];

		if (isVisualContentLoop) {
			// we will use the slideIndex as part of the id
			// just in case the same gallery file is in the
			// same slidedeck more than once for any reason
			slide.slideID = `${providerID}::${slide.file}::${slide.slideIndex}`;
		} else {
			slide.slideID = `${slide.file}::${slide.order}::${slide.data?.slide_id || ''}`;
		}

		// slide.provider = this;

		// TODO: should we change this too? R19 doesn't seem to
		// TODO: like it when we pass objects into props/state
		// TODO: especially if they have nested objects/functions
		// TODO: like the full provider object being passed here
		slide.provider = {
			providerID: this.providerID,
			providerName: (isVisualContentLoop) ? 'slide_deck' : this.constructor.providerName,
			slideshowName: this.props.custom_content_sidebar_label || '',
			skipper:    this.skipper.bind(this),
			afterNext: this.afterNext.bind(this),
			beforeNextPick: this.beforeNextPick.bind(this),
		};
		
		// Validate day scheduling configuration
		if (slide.use_days) {
			const validation = this.validateDayScheduling(slide);
			if (!validation.isValid) {
				console.warn('PELICAN::DAY_SCHEDULE_VALIDATION:', {
					slideId: slide.slideID,
					description: slide.description,
					warnings: validation.warnings
				});
			} else if (validation.dayNames) {
				console.log('PELICAN::DAY_SCHEDULE_CONFIG:', {
					slideId: slide.slideID,
					description: slide.description,
					allowedDays: validation.dayNames
				});
			}
		}
		
		return slide;
	}

	makeProvider(slides = [], folderLimitsParam = null) {
		const {
			provider = {},
			providerID,
			limit: maxSlides,
			order: providerOrder,
			sort: sortType
		} = this.props;

		// Use passed folderLimits if provided, otherwise fall back to state
		// (state may not be committed yet during initial fetchSlides)
		const folderLimits = folderLimitsParam ?? this.state.folderLimits;

		// Calculate effective visible slide count accounting for folder windowing.
		// Folder slides are flattened in the slides array, but only folder_slots
		// are visible per loop. So we count non-folder slides + sum of folder_slots.
		let effectiveSlideCount = 0;
		const countedFolders = new Set();
		for (const slide of slides) {
			if (slide.folder_id) {
				if (!countedFolders.has(slide.folder_id)) {
					countedFolders.add(slide.folder_id);
					effectiveSlideCount += folderLimits[slide.folder_id] || 0;
				}
			} else {
				effectiveSlideCount++;
			}
		}

		return {
			providerID: providerID,
			providerName: this.constructor.providerName, // Add the static providerName to the provider object
			options:    {
				maxSlides:     maxSlides,
				providerOrder: providerOrder,
				sortType:      sortType || 'sequential'
			},
			is_backfill: provider.is_backfill,
			order: provider.order,
			slots: maxSlides, // VCL slots for missing slide calculation
			effectiveSlideCount, // Visible slides per loop (accounts for folder windowing)
			slides:  slides,
			skipper: this.skipper.bind(this),
			afterNext: this.afterNext.bind(this),
			beforeNextPick: this.beforeNextPick.bind(this),
		};
	}

	formatTime(i) {
		if (i < 10) {
			i = `0${i}`;
		}

		return i;
	}

	getTime() {
		let now = new Date();
		let h = now.getHours();
		let m = now.getMinutes();
		let s = now.getSeconds();

		h = this.formatTime(h);
		m = this.formatTime(m);
		s = this.formatTime(s);

		let time = `${h}:${m}:${s}`;
		if (time.length === 7) {
			time = '0' + time;
		}

		return time;
	}

	vclScheduleSkipper(slide) {
		// shouldSkipSlide honors the scheduled === false toggle short-circuit,
		// so slot-limiting (limitSlidesToSlots) and this path agree
		return shouldSkipSlide(slide);
	}

	scheduleSkipper(slide) {
		// New format: check for 'scheduled' field
		if (slide.scheduled !== undefined) {
			if (!slide.scheduled) return false;
			return shouldSkipSlide(slide);
		}

		// Old format
		if (!slide.scheduling) {
			return false;
		}

		const now = new Date();

		// Debug logging for day-based scheduling
		if (slide.use_days && slide.days) {
			// console.log('PELICAN::DAY_SCHEDULE_DEBUG:', {
			// 	slideId: slide.slideID,
			// 	currentDay: now.getDay(),
			// 	currentDayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][now.getDay()],
			// 	allowedDays: slide.days,
			// 	description: slide.description
			// });
		}

		if (slide.use_dates) {
			if (slide.start_date) {
				const startDate = new Date(slide.start_date);

				// skip if we are before the startDate
				if (now < startDate) {
					return true;
				}
			}

			if (slide.end_date) {
				const endDate = new Date(slide.end_date);

				// expire the slide the day after the end date
				endDate.setHours(23);
				endDate.setMinutes(59);
				endDate.setSeconds(59);

				if (now > endDate) {
					return true;
				}
			}
		}

		if (slide.use_times) {
			const hours = String(now.getHours()).padStart(2, '0');
			const minutes = String(now.getMinutes()).padStart(2, '0');
			const seconds = String(now.getSeconds()).padStart(2, '0');
			const nowTime = `${hours}:${minutes}:${seconds}`;

			let endTime, startTime;

			if (slide.start_time) {
				startTime = slide.start_time;
				if (startTime.length === 7) {
					startTime = '0' + startTime;
				}
			}

			if (slide.end_time) {
				endTime = slide.end_time;
				if (endTime.length === 7) {
					endTime = '0' + endTime;
				}
			}

			// endTime is less than startTime
			if (startTime && endTime && endTime < startTime) {
				if (nowTime < startTime && nowTime >= endTime) {
					return true;
				}
			} else {
				if (startTime && nowTime < startTime) {
					return true;
				}

				if (endTime && nowTime > endTime) {
					return true;
				}
			}
		}

		if (slide.use_days) {
			const today = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

			const days = slide.days;
			if (days && Array.isArray(days) && days.length > 0) {
				// Check if today is NOT in the allowed days array
				// Using includes() for better readability than bitwise operators
				if (!days.includes(today)) {
					return true;
				}
			}
		}

		return false;
	}

	// skipper(slide, peek?)
	//
	// peek = { afterSlide, allowWrap } (optional, passed by Brood when it
	// pre-computes the slide AFTER the one about to play, so the crossfade can
	// pre-mount it — DEV-12201). In peek mode the folder gate is evaluated as if
	// `afterSlide` (one of THIS provider's slides) had just completed a
	// successful play: its folder's cursor is one step further and has one more
	// play this loop; a non-folder afterSlide is the loop boundary, so every
	// folder's plays reset; the pure-folder quota wrap is simulated only when
	// Brood says this deck is the sole provider (allowWrap). Pure — reads the
	// instance shadows, mutates nothing. Without peek the live state is used.
	// Known approximation: the live wrap also ignores non-folder slides that are
	// scheduled off / in backoff (hasPlayableNonFolder); peek has no broodSkipper
	// and uses plain presence, so that deck shape falls back to the fresh-mount
	// fade instead of a pre-mount.
	skipper(currSlide, peek = null) {
		const {
			isVisualContentLoop,
			allow_urls
		} = this.props;

		const {
			folderLimits,
		} = this.state;

		// Only this provider's own slides move this provider's state; a peek
		// about another provider's slide (another deck, Nova, backfill) is a
		// no-op here — beforeNextPick only ever runs for the current provider.
		const ownProviderID = this.props.providerID || this.providerID;
		const afterSlide = (peek && peek.afterSlide && peek.afterSlide.providerID === ownProviderID)
			? peek.afterSlide
			: null;
		const allowWrap = !(peek && peek.allowWrap === false);

		if (currSlide.folder_id) {
			const folderId = currSlide.folder_id;
			// Cursor-based rotation: allow at most ONE folder slide at a time —
			// the one whose gallery_file_index matches the cursor — and only
			// while the loop quota (folder_slots) hasn't been hit.
			//
			// Reading from instance shadows: beforeNextPick mutates these
			// synchronously, and Brood's picker calls us inline inside
			// slideshow.next() before any setState commit could land.
			let cursor = this._folderCursor[folderId];
			let playsThisLoop = this._folderPlaysThisLoop[folderId] || 0;
			const folderSlots = folderLimits[folderId] || 1;

			if (cursor === undefined) {
				// Folder not yet initialized — should only happen on first
				// render before fetchSlides seeds the cursor. Skip defensively.
				return true;
			}

			if (afterSlide) {
				// Mirror beforeNextPick's success path for the about-to-play slide.
				if (afterSlide.folder_id === folderId) {
					const folderLength = afterSlide.folder_length || currSlide.folder_length || 1;
					cursor = (cursor % folderLength) + 1;
					playsThisLoop += 1;
					// Pure-folder deck: beforeNextPick resets plays in place once
					// every folder is at quota (no non-folder play will ever fire
					// the loop-boundary reset). Mirror it so the wrap-around
					// successor can still be pre-mounted.
					if (playsThisLoop >= folderSlots) {
						const hasNonFolder = this.state.slides.some((s) => {
							return !s.folder_id;
						});
						const othersAtQuota = Object.keys(this._folderPlaysThisLoop).every((key) => {
							const otherId = isNaN(Number(key)) ? key : Number(key);
							if (otherId === folderId) {
								return true;
							}
							return (this._folderPlaysThisLoop[otherId] || 0) >= (folderLimits[otherId] || 1);
						});
						if (allowWrap && !hasNonFolder && othersAtQuota) {
							playsThisLoop = 0;
						}
					}
				} else if (!afterSlide.folder_id) {
					playsThisLoop = 0;
				}
			}

			if (currSlide.gallery_file_index !== cursor) {
				return true;
			}
			if (playsThisLoop >= folderSlots) {
				return true;
			}
		} else if (this._pendingFolderSubstitute !== null
			&& !(afterSlide && afterSlide.folder_id === this._pendingFolderSubstitute)) {
			// (peek: a successful play of the pending folder's slide clears the
			// substitute, so non-folder slides are eligible again after it.)
			// A folder slide just errored and is awaiting a substitute play.
			// Reject non-folder slides so the picker doesn't loop-back to a
			// non-folder slide between the error and its substitute. Cleared
			// when any folder slide plays successfully (in beforeNextPick).
			return true;
		}

		if (isVisualContentLoop) {
			if (this.vclScheduleSkipper(currSlide)) {
				return true;
			}
		} else {
			if (this.scheduleSkipper(currSlide)) {
				return true;
			}
		}

		if (!allow_urls && currSlide.kind === 'html') {
			return true;
		}

		return false;
	}

	// Helper method to validate day scheduling configuration
	validateDayScheduling(slide) {
		if (!slide.use_days || !slide.days) {
			return { isValid: true, warnings: [] };
		}

		const warnings = [];
		const validDays = [0, 1, 2, 3, 4, 5, 6]; // Sunday = 0, Monday = 1, etc.

		if (!Array.isArray(slide.days)) {
			warnings.push('slide.days should be an array');
			return { isValid: false, warnings };
		}

		if (slide.days.length === 0) {
			warnings.push('slide.days array is empty - slide will never show');
		}

		for (const day of slide.days) {
			if (!validDays.includes(day)) {
				warnings.push(`Invalid day value: ${day}. Valid values are 0-6 (0=Sunday, 6=Saturday)`);
			}
		}

		return {
			isValid: warnings.length === 0,
			warnings,
			dayNames: slide.days.map(day => 
				['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day] || `Invalid(${day})`
			)
		};
	}

	// Called by Brood.next() BEFORE the slide picker runs. Drives the
	// cursor-based folder rotation model:
	//
	//   - Folder slide just played (success) → advance cursor, increment
	//     playsThisLoop. When playsThisLoop hits folder_slots, skipper rejects
	//     every folder slide until a non-folder slide plays (loop wrap).
	//   - Folder slide just errored → advance cursor (treat the slot as
	//     consumed for sequencing purposes), but DON'T increment playsThisLoop
	//     (the slot is still owed a successful play, picker will return the
	//     next cursor slide to substitute).
	//   - Non-folder slide just played → if any folder has playsThisLoop > 0,
	//     reset all to 0. This is the loop boundary signal.
	//   - Pre-picker: advance cursor past any slide currently rejected by
	//     Brood's backoff-aware skipper so the picker's next call finds a
	//     playable cursor target (cleanly handles broken trailing slides).
	//   - Pure-folder deck (no non-folder slides in the deck): when every
	//     folder hits its slot quota, reset playsThisLoop in place since no
	//     non-folder slide will play to trigger the reset naturally.
	//
	// All mutations are synchronous via instance shadows; setState mirror is
	// kept only for devtools introspection. We do NOT call
	// updateSlideshowProvider — cursor changes are Pelican-internal and the
	// picker reads them transparently via skipper's closure.
	beforeNextPick(currSlide, broodSkipper, slidePlayInfo = {}) {
		// --- 1. Update state for the slide that just finished ---
		// Treat `skipped` the same as `error` for quota accounting: in both
		// cases the slide didn't actually complete a successful play, so the
		// slot is still owed and playsThisLoop must NOT increment.
		const didNotPlay = !!(slidePlayInfo.error || slidePlayInfo.skipped);
		if (currSlide && currSlide.folder_id) {
			const folderId = currSlide.folder_id;
			const folderLength = currSlide.folder_length;
			if (folderLength > 0) {
				if (didNotPlay) {
					// Slot is still owed. Advance cursor (to skip this slide
					// next iteration) but don't increment plays. Set substitute
					// pending so the next picker pass favors a folder slide
					// over a loop-back to a non-folder slide — but only while
					// the folder is below its loop quota: a skip/error AT quota
					// owes nothing, and arming a substitute then makes the deck
					// look pure-folder and forces a fake mid-loop quota reset.
					const playsThisLoop = this._folderPlaysThisLoop[folderId] || 0;
					const folderSlots = this.state.folderLimits[folderId] || 1;
					if (playsThisLoop < folderSlots) {
						this._pendingFolderSubstitute = folderId;
					}
				} else {
					// Successful play — slot fulfilled. Clear any pending
					// substitute (we just played one).
					this._folderPlaysThisLoop[folderId] =
						(this._folderPlaysThisLoop[folderId] || 0) + 1;
					if (this._pendingFolderSubstitute === folderId) {
						this._pendingFolderSubstitute = null;
					}
				}
				const cursor = this._folderCursor[folderId] || 1;
				this._folderCursor[folderId] = (cursor % folderLength) + 1;
			}
		} else if (currSlide && !currSlide.folder_id && !didNotPlay) {
			// Non-folder slide actually played. If any folder had successful
			// plays this loop, this is the loop-boundary signal — reset for the
			// next loop. Skipping the reset on error/skip avoids fake loop wraps
			// when a non-folder slide is yanked before displaying.
			for (const folderId of Object.keys(this._folderPlaysThisLoop)) {
				if (this._folderPlaysThisLoop[folderId] > 0) {
					this._folderPlaysThisLoop[folderId] = 0;
				}
			}
		}

		// --- 2. Pre-picker cursor advance past unplayable cursor targets ---
		// Picker needs the cursor to point at a playable slide. If the cursor
		// currently points at a backoff'd / scheduled-off slide, advance past it
		// (bounded by folder length to avoid infinite loops on all-broken folders).
		//
		// Side product: folderPlayability[folderId] = whether the probe found ANY
		// playable slide in a below-quota folder. Step 3 uses it — a folder with
		// no playable slides can never reach quota and must not block the
		// pure-folder loop wrap (DEV-11978). Folders at quota are never probed
		// (they don't need it and can't block the wrap anyway).
		//
		// Invariant: this loop iterates _folderCursor keys while step 3 iterates
		// _folderPlaysThisLoop keys — safe only because the two maps are seeded
		// and deleted in lockstep (fetchSlides). A plays-map entry with no cursor
		// entry would go unprobed and conservatively block the wrap.
		const folderPlayability = {};
		if (typeof broodSkipper === 'function') {
			for (const folderIdKey of Object.keys(this._folderCursor)) {
				// Object.keys yields strings, but slide.folder_id is a number
				// from the bronco JSON. Coerce so comparisons / lookups match.
				const folderId = isNaN(Number(folderIdKey)) ? folderIdKey : Number(folderIdKey);
				const playsThisLoop = this._folderPlaysThisLoop[folderId] || 0;
				const folderSlots = this.state.folderLimits[folderId] || 1;
				// If the loop quota is already met, the picker isn't going to pick
				// from this folder anyway — no point advancing the cursor.
				if (playsThisLoop >= folderSlots) {
					continue;
				}
				const folderSlides = this.state.slides.filter((s) => {
					return s.folder_id === folderId;
				});
				if (folderSlides.length === 0) {
					folderPlayability[folderId] = false;
					continue;
				}
				const folderLength = folderSlides[0].folder_length || folderSlides.length;
				let safety = 0;
				let foundPlayable = false;
				while (safety < folderLength) {
					const cursor = this._folderCursor[folderId];
					const slideAtCursor = folderSlides.find((s) => {
						return s.gallery_file_index === cursor;
					});
					// If we can't find the slide at the cursor, advance defensively
					// (data inconsistency we shouldn't get stuck on).
					if (!slideAtCursor) {
						this._folderCursor[folderId] = (cursor % folderLength) + 1;
						safety++;
						continue;
					}
					// broodSkipper returns true if Brood would skip the slide
					// (backoff / Pelican.skipper-driven cursor mismatch / etc.).
					// Since slideAtCursor MATCHES the cursor and playsThisLoop <
					// folder_slots, Pelican.skipper won't reject it; any
					// rejection comes from Brood (backoff/etc.).
					if (!broodSkipper(slideAtCursor)) {
						foundPlayable = true;
						break;
					}
					this._folderCursor[folderId] = (cursor % folderLength) + 1;
					safety++;
				}
				folderPlayability[folderId] = foundPlayable;
				// If every folder slide is unplayable (e.g. entire folder in
				// backoff), drop any pending substitute for this folder so the
				// picker can return non-folder slides and the deck doesn't lock up.
				if (!foundPlayable && this._pendingFolderSubstitute === folderId) {
					this._pendingFolderSubstitute = null;
				}
			}
		}

		// --- 3. Pure-folder (or effectively-pure-folder) deck loop wrap ---
		// If the deck has NO playable non-folder slides (none in the deck, OR
		// every non-folder slide is currently in backoff / scheduled-off), no
		// non-folder play will ever fire the loop-boundary reset above. Detect
		// "all folders at quota" and reset playsThisLoop in place to start a
		// fresh loop, otherwise the picker will lock up on the last folder
		// slide once quota is hit and the non-folder loop-back is unplayable.
		const hasPlayableNonFolder = this.state.slides.some((s) => {
			if (s.folder_id) return false;
			return typeof broodSkipper !== 'function' || !broodSkipper(s);
		});
		// Reported back to Brood: true when the pure-folder wrap below fired,
		// i.e. this deck just completed a full folder round. Brood treats it
		// as the provider's cycle-complete signal so the deck yields AT the
		// round boundary instead of one slide past it — without this, the
		// re-opened cursor slide plays immediately (attempts are per-visit,
		// not per-round) and every steady-state round phase-shifts to
		// [PM, VID, backfill] instead of deck order [VID, PM, backfill].
		// (DEV-11981)
		let loopWrapped = false;
		if (!hasPlayableNonFolder) {
			const folderIdKeys = Object.keys(this._folderPlaysThisLoop);
			let allAtQuota = folderIdKeys.length > 0;
			for (const folderIdKey of folderIdKeys) {
				const folderId = isNaN(Number(folderIdKey)) ? folderIdKey : Number(folderIdKey);
				const folderSlots = this.state.folderLimits[folderId] || 1;
				if ((this._folderPlaysThisLoop[folderId] || 0) < folderSlots) {
					// A below-quota folder only blocks the loop wrap if it can
					// still play. A folder whose slides are ALL unplayable
					// (schedule-expired / in backoff) can never reach quota —
					// counting it froze rotation permanently once the playable
					// folders hit quota (DEV-11978). Unprobed folders
					// (folderPlayability undefined) keep the conservative
					// blocking behavior.
					if (folderPlayability[folderId] !== false) {
						allAtQuota = false;
						break;
					}
				}
			}
			if (allAtQuota) {
				for (const folderIdKey of folderIdKeys) {
					const folderId = isNaN(Number(folderIdKey)) ? folderIdKey : Number(folderIdKey);
					this._folderPlaysThisLoop[folderId] = 0;
				}
				loopWrapped = true;
			}
		}

		// React state sync — devtools-only, picker reads instance shadows.
		this.setState({ folderCursor: { ...this._folderCursor } });

		return { loopWrapped };
	}

	// afterNext is kept as a no-op for VCL slide-deck providers. Folder
	// rotation lives entirely in beforeNextPick (which fires before the picker,
	// not after).
	afterNext() {
		return;
	}

	render() {
		return null;
	}
}

export default PelicanProviderComponent;
